import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { useWSChannel } from '../../../shared/ws-client/useWSChannel.js';
import { getDealDetailQueryKey, getDealsQueryKey, listDeals, type DealStageChangedEvent, type DealListItem, type ListDealsFilters } from '../api/deals.client.js';
import { buildKanbanColumns } from '../lib/kanban.js';

export interface UseDealsResult {
  deals: DealListItem[];
  columns: ReturnType<typeof buildKanbanColumns>;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

export function useDeals(filters: ListDealsFilters = {}): UseDealsResult {
  const scope = useCurrentScope();
  const queryClient = useQueryClient();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const query = useQuery({
    queryKey: getDealsQueryKey(workspaceId, filters),
    queryFn: () => listDeals(filters),
    enabled: scope !== null,
    retry: false,
  });

  useWSChannel<DealStageChangedEvent>('/ws/deals', {
    enabled: scope !== null,
    onMessage: (event) => {
      if (event.type !== 'deal.stage.changed' || event.workspaceId !== workspaceId) {
        return;
      }

      void queryClient.invalidateQueries({ queryKey: getDealsQueryKey(workspaceId) });
      void queryClient.invalidateQueries({ queryKey: getDealDetailQueryKey(workspaceId, event.dealId) });
    },
  });

  const deals = query.data ?? [];
  const columns = useMemo(() => buildKanbanColumns(deals), [deals]);

  return {
    deals,
    columns,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error instanceof Error ? query.error : null,
    refetch: query.refetch,
  };
}
